import { useCallback, type MouseEvent } from 'react';
import { useNavigationTabs } from '@/hooks';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';
import type { Note } from '@/types/notes';

export function useNoteDetailPanelNavigation(selectedNote: Note | null) {
  const {
    tabs,
    activeTabId,
    openTab,
    closeTab,
    switchTab,
    closeAllTabs,
    getTabByTaskId,
  } = useNavigationTabs();

  const selectTask = useGeoMarkupStore((state) => state.tasks.selectTask);
  const selectNote = useGeoMarkupStore((state) => state.notes.selectNote);
  const { openArtifact, closeOverlay } = useArtifactNavigation();

  const handleTaskNavigate = useCallback(
    (taskId: string, title: string, shortId?: string) => {
      const existingTab = getTabByTaskId(taskId);

      if (existingTab) {
        switchTab(existingTab.id);
      } else {
        if (tabs.length === 0 && selectedNote) {
          openTab(
            selectedNote.id,
            selectedNote.title,
            'note',
            selectedNote.shortId
          );
        }
        openTab(taskId, title, 'task', shortId);
      }

      selectTask(taskId);
      openArtifact('tasks', 'detail', shortId ?? taskId);
    },
    [
      getTabByTaskId,
      switchTab,
      tabs.length,
      selectedNote,
      openTab,
      selectTask,
      openArtifact,
    ]
  );

  const navigateToTab = useCallback(
    (tab: { type: 'task' | 'note'; taskId: string; shortId?: string }) => {
      if (tab.type === 'task') {
        selectTask(tab.taskId);
        openArtifact('tasks', 'detail', tab.shortId ?? tab.taskId);
      } else {
        selectNote(tab.taskId);
        if (tab.taskId !== selectedNote?.id) {
          openArtifact('notes', 'detail', tab.shortId ?? tab.taskId);
        }
      }
    },
    [selectTask, selectNote, openArtifact, selectedNote?.id]
  );

  const handleTabClick = useCallback(
    (tabId: string) => {
      const freshState = useGeoMarkupStore.getState();
      const tab = freshState.taskNavigation.tabs.find(
        (navTab) => navTab.id === tabId
      );
      if (!tab) return;

      switchTab(tabId);
      navigateToTab(tab);
    },
    [switchTab, navigateToTab]
  );

  const handleTabClose = useCallback(
    (tabId: string, event: MouseEvent) => {
      event.stopPropagation();
      closeTab(tabId);

      const freshState = useGeoMarkupStore.getState();
      const newActiveTabId = freshState.taskNavigation.activeTabId;
      const newActiveTab = freshState.taskNavigation.tabs.find(
        (navTab) => navTab.id === newActiveTabId
      );

      if (newActiveTab) {
        navigateToTab(newActiveTab);
      } else {
        closeOverlay();
        closeAllTabs();
      }
    },
    [closeTab, navigateToTab, closeOverlay, closeAllTabs]
  );

  const handleClose = useCallback(() => {
    closeOverlay();
    closeAllTabs();
  }, [closeOverlay, closeAllTabs]);

  return {
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handleClose,
    handleTaskNavigate,
    openArtifact,
    closeOverlay,
  };
}
